import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronDown,
  FiImage,
  FiFilter,
  FiRotateCcw,
  FiDownload,
  FiSearch,
  FiMessageCircle,
  FiTag,
  FiZap,
  FiBarChart2,
  FiUsers,
  FiShare2,
  FiRepeat,
  FiBookmark,
  FiUser,
  FiLayers,
  FiPlus,
  FiArrowLeft,
  FiChevronLeft,
  FiChevronRight,
  FiX,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import DashboardLayout from "../../UI/Layout/DashboardLayout";
import {
  fetchInstagramAccountData,
  fetchInstagramMediaComments,
  fetchInstagramReplyReports,
  replyToComment,
} from "./instagramApi";
import styles from "./Instagram.module.css";

// yyyy-mm-dd for <input type="date"> — used as the default "today" range.
const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "manual", label: "Manual" },
  { value: "automatic", label: "Automatic" },
];

const INSTAGRAM_AUTOMATION_ROUTE = "/dashboard/instagram-automation";

const CHART_BAR_AREA_HEIGHT = 180;
const CHART_LABEL_GUTTER = 26;
const CHART_PLOT_HEIGHT = CHART_BAR_AREA_HEIGHT - CHART_LABEL_GUTTER;
const CHART_MIN_COLUMN_WIDTH = 44;
const CHART_YAXIS_WIDTH = 34;
const TABLE_PAGE_SIZE = 10;

const getNiceTicks = (rawMax, targetTickCount = 5) => {
  const safeMax = Math.max(1, rawMax);
  const rawStep = safeMax / (targetTickCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const residual = rawStep / magnitude;

  let niceStep;
  if (residual > 5) niceStep = 10 * magnitude;
  else if (residual > 2) niceStep = 5 * magnitude;
  else if (residual > 1) niceStep = 2 * magnitude;
  else niceStep = magnitude;
  niceStep = Math.max(1, Math.round(niceStep));

  const niceMax = Math.ceil(safeMax / niceStep) * niceStep;
  const ticks = [];
  for (let t = 0; t <= niceMax; t += niceStep) ticks.push(t);
  return ticks;
};

const formatChartDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatChartDateLong = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};


const buildSmoothLinePath = (points) => {
  if (!points.length) return "";
  if (points.length === 1) return `M${points[0].x},${points[0].y}`;

  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i === 0 ? i : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
};

const buildSmoothAreaPath = (points) => {
  const line = buildSmoothLinePath(points);
  if (!line) return "";
  const first = points[0];
  const last = points[points.length - 1];
  return `${line} L${last.x},100 L${first.x},100 Z`;
};

/* ------------------------------------------------------------------ */
/* Chart tooltip — small floating card shown above the hovered column */
/* ------------------------------------------------------------------ */
const ChartTooltip = ({ point, hasSentiment, bottomOffset }) => (
  <div
    className={styles.chartTooltip}
    style={{ bottom: `${bottomOffset}px` }}
  >
    <div className={styles.chartTooltipDate}>
      {formatChartDateLong(point.date)}
    </div>
    <div className={styles.chartTooltipRow}>
      <span className={`${styles.chartTooltipDot} ${styles.chartTooltipDotTotal}`} />
      Total: <strong>{point.total}</strong>
    </div>
    {hasSentiment && (
      <>
        <div className={styles.chartTooltipRow}>
          <span className={`${styles.chartTooltipDot} ${styles.chartTooltipDotPositive}`} />
          Positive: <strong>{point.positive}</strong>
        </div>
        <div className={styles.chartTooltipRow}>
          <span className={`${styles.chartTooltipDot} ${styles.chartTooltipDotNeutral}`} />
          Neutral: <strong>{point.neutral}</strong>
        </div>
        <div className={styles.chartTooltipRow}>
          <span className={`${styles.chartTooltipDot} ${styles.chartTooltipDotNegative}`} />
          Negative: <strong>{point.negative}</strong>
        </div>
      </>
    )}
    {/* little downward-pointing arrow */}
    <div className={styles.chartTooltipArrow} />
  </div>
);

const AreaChartTooltip = ({ point, bottomOffset }) => (
  <div
    className={styles.chartTooltip}
    style={{ bottom: `${bottomOffset}px` }}
  >
    <div className={styles.chartTooltipDate}>
      {formatChartDateLong(point.date)}
    </div>
    <div className={styles.chartTooltipRow}>
      <span className={styles.chartTooltipEmoji} aria-hidden="true">
        😠
      </span>
      <span className={styles.chartTooltipLabelNegative}>
        Negative Comments
      </span>
      : <strong>{point.negative}</strong>
    </div>
    <div className={styles.chartTooltipRow}>
      <span className={styles.chartTooltipEmoji} aria-hidden="true">
        😊
      </span>
      <span className={styles.chartTooltipLabelPositive}>
        Positive Comments
      </span>
      : <strong>{point.positive}</strong>
    </div>
    <div className={styles.chartTooltipArrow} />
  </div>
);

const CHART_SKELETON_HEIGHTS = [34, 52, 68, 88, 108, 128, 148, 138, 116, 96, 76, 58];
const ChartSkeleton = () => (
  <div
    className={styles.chartSkeletonWrap}
    style={{ height: `${CHART_BAR_AREA_HEIGHT}px` }}
    aria-label="Updating chart…"
  >
    <div className={styles.chartSkeletonBars}>
      {CHART_SKELETON_HEIGHTS.map((height, index) => (
        <span
          key={index}
          className={`${styles.skeletonBlock} ${styles.chartSkeletonBar}`}
          style={{ height: `${height}px` }}
        />
      ))}
    </div>
  </div>
);

const DaywiseChart = ({ data, mode, hasSentiment }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!data.length) {
    return (
      <p className={styles.commentStatus}>No data in the selected range.</p>
    );
  }

  const rawMax = Math.max(...data.map((d) => d.total), 1);
  const ticks = getNiceTicks(rawMax); // ascending, e.g. [0, 3, 6, 9, 12]
  const niceMax = ticks[ticks.length - 1] || 1;
  const descendingTicks = [...ticks].reverse(); // top-to-bottom, for rendering

  const minTotalWidth =
    CHART_YAXIS_WIDTH + Math.max(data.length * CHART_MIN_COLUMN_WIDTH, 100);

  const barHeightPx = (value) => (value / niceMax) * CHART_PLOT_HEIGHT;


  const PLOT_FRACTION = CHART_PLOT_HEIGHT / CHART_BAR_AREA_HEIGHT;
  const valueToY = (value) => 100 - (value / niceMax) * 100 * PLOT_FRACTION;
  const pointX = (index) => ((index + 0.5) / data.length) * 100;

  const positivePoints = data.map((point, index) => ({
    x: pointX(index),
    y: valueToY(point.positive),
  }));
  const negativePoints = data.map((point, index) => ({
    x: pointX(index),
    y: valueToY(point.negative),
  }));
  const totalPoints = data.map((point, index) => ({
    x: pointX(index),
    y: valueToY(point.total),
  }));

  return (
    <div
      className={styles.chartWrap}
      style={{ minWidth: `${minTotalWidth}px` }}
    >
      <div className={styles.chartPlotRow}>
        {/* Y-axis — sticky so it stays visible while the bars scroll
            horizontally on wide date ranges. Confined to the bottom
            CHART_PLOT_HEIGHT of the row via padding-top so its topmost
            tick label never pokes above the row (see CHART_LABEL_GUTTER). */}
        <div
          className={styles.chartYAxis}
          style={{
            height: `${CHART_BAR_AREA_HEIGHT}px`,
            paddingTop: `${CHART_LABEL_GUTTER}px`,
          }}
        >
          {descendingTicks.map((tick) => (
            <span key={tick} className={styles.chartYAxisTick}>
              {tick}
            </span>
          ))}
        </div>

        <div
          className={styles.chartBarsRow}
          style={{ height: `${CHART_BAR_AREA_HEIGHT}px` }}
        >
          <div
            className={styles.chartGridlines}
            style={{ top: `${CHART_LABEL_GUTTER}px` }}
          >
            {descendingTicks.map((tick) => (
              <div key={tick} className={styles.chartGridline} />
            ))}
          </div>

          <div className={styles.chartColumns}>
            {data.map((point, index) => {
              const isHovered = hoveredIndex === index;
              const barTop = barHeightPx(point.total);
              // Clamp so the tooltip's own box (~64px for a total-only
              // tooltip, ~124px with the full sentiment breakdown on the
              // stacked-bar view, ~90px for the shorter Negative/Positive
              // Area Trend tooltip, plus a buffer for the drop-shadow's
              // visual bleed) never gets pushed above the row —
              // chartScrollArea clips vertical overflow, so an
              // unclamped/underestimated offset makes the tooltip get cut
              // off for the tallest bar(s)/highest points.
              const estimatedTooltipHeight =
                mode === "area" && hasSentiment ? 90 : hasSentiment ? 124 : 68;
              const tooltipBottomOffset = Math.max(
                6,
                Math.min(barTop + 14, CHART_BAR_AREA_HEIGHT - estimatedTooltipHeight),
              );

              return (
                <div
                  key={point.date}
                  className={styles.chartColumn}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onFocus={() => setHoveredIndex(index)}
                  onBlur={() => setHoveredIndex(null)}
                  tabIndex={0}
                  role="img"
                  aria-label={`${formatChartDateLong(point.date)}: ${point.total} total`}
                >
                  {isHovered &&
                    (mode === "area" && hasSentiment ? (
                      <AreaChartTooltip
                        point={point}
                        bottomOffset={tooltipBottomOffset}
                      />
                    ) : (
                      <ChartTooltip
                        point={point}
                        hasSentiment={hasSentiment}
                        bottomOffset={tooltipBottomOffset}
                      />
                    ))}

                  {/* subtle full-height hover backdrop so it's easy to
                      tell which column is being targeted, even between
                      bars */}
                  <div
                    className={`${styles.chartColumnHoverBg} ${
                      isHovered ? styles.chartColumnHoverBgActive : ""
                    }`}
                  />

                  {/* always-visible value label, like the count labels
                      printed above each bar in a typical distribution
                      chart — only shown for the bar chart. The Area Trend
                      view reads its counts from the hover tooltip instead,
                      to keep the curve clean like the reference design. */}
                  {mode === "stacked" && (
                    <span
                      className={`${styles.chartValueLabel} ${
                        isHovered ? styles.chartValueLabelActive : ""
                      }`}
                      style={{ bottom: `${barTop + 6}px` }}
                    >
                      {point.total}
                    </span>
                  )}

                  {mode === "stacked" ? (
                    hasSentiment ? (
                      <div
                        className={`${styles.chartStack} ${
                          isHovered ? styles.chartStackHover : ""
                        }`}
                      >
                        {point.negative > 0 && (
                          <div
                            className={styles.chartSegNegative}
                            style={{ height: `${barHeightPx(point.negative)}px` }}
                          />
                        )}
                        {point.neutral > 0 && (
                          <div
                            className={styles.chartSegNeutral}
                            style={{ height: `${barHeightPx(point.neutral)}px` }}
                          />
                        )}
                        {point.positive > 0 && (
                          <div
                            className={styles.chartSegPositive}
                            style={{ height: `${barHeightPx(point.positive)}px` }}
                          />
                        )}
                      </div>
                    ) : (
                      <div
                        className={`${styles.chartSegTotal} ${
                          isHovered ? styles.chartSegTotalHover : ""
                        }`}
                        style={{ height: `${barTop}px` }}
                      />
                    )
                  ) : hasSentiment ? (
                    // Area Trend + sentiment: no marker drawn at rest — the
                    // two curves below (positive/negative) carry the shape,
                    // and a crosshair + a dot on each curve only appears
                    // for the hovered day, matching the reference design.
                    isHovered && (
                      <>
                        <div className={styles.chartCrosshairLine} />
                        <span
                          className={styles.chartAreaDotPositive}
                          style={{ bottom: `${barHeightPx(point.positive)}px` }}
                        />
                        <span
                          className={styles.chartAreaDotNegative}
                          style={{ bottom: `${barHeightPx(point.negative)}px` }}
                        />
                      </>
                    )
                  ) : (
                    <div
                      className={styles.chartAreaDotWrap}
                      style={{ height: `${barTop}px` }}
                    >
                      <span
                        className={`${styles.chartAreaDot} ${
                          isHovered ? styles.chartAreaDotHover : ""
                        }`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {mode === "area" && (
            <svg
              className={styles.chartLineOverlay}
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="chartAreaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(109, 61, 245, 0.32)" />
                  <stop offset="100%" stopColor="rgba(109, 61, 245, 0)" />
                </linearGradient>
                <linearGradient id="chartAreaPositiveFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(18, 183, 106, 0.4)" />
                  <stop offset="100%" stopColor="rgba(18, 183, 106, 0.02)" />
                </linearGradient>
                <linearGradient id="chartAreaNegativeFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(224, 36, 94, 0.5)" />
                  <stop offset="100%" stopColor="rgba(224, 36, 94, 0.05)" />
                </linearGradient>
              </defs>

              {hasSentiment ? (
                <>
                  {/* Positive drawn first (bottom of the stack) so the
                      thin negative band reads as sitting on top of it
                      near the baseline, like the reference chart. */}
                  <path
                    d={buildSmoothAreaPath(positivePoints)}
                    fill="url(#chartAreaPositiveFill)"
                    stroke="none"
                  />
                  <path
                    d={buildSmoothLinePath(positivePoints)}
                    className={styles.chartAreaLinePositive}
                  />
                  <path
                    d={buildSmoothAreaPath(negativePoints)}
                    fill="url(#chartAreaNegativeFill)"
                    stroke="none"
                  />
                  <path
                    d={buildSmoothLinePath(negativePoints)}
                    className={styles.chartAreaLineNegative}
                  />
                </>
              ) : (
                <>
                  <path
                    d={buildSmoothAreaPath(totalPoints)}
                    fill="url(#chartAreaFill)"
                    stroke="none"
                  />
                  <path
                    d={buildSmoothLinePath(totalPoints)}
                    className={styles.chartAreaLine}
                  />
                </>
              )}
            </svg>
          )}
        </div>
      </div>

      <div className={styles.chartLabelsRowWrap}>
        <div
          className={styles.chartYAxisSpacer}
          aria-hidden="true"
        />
        <div className={styles.chartLabelsRow}>
          {data.map((point, index) => (
            <div
              key={point.date}
              className={`${styles.chartColumnLabel} ${
                hoveredIndex === index ? styles.chartColumnLabelActive : ""
              }`}
            >
              {formatChartDate(point.date)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Reusable table pagination bar — only rendered when there's more    */
/* than one page. Shows a "x–y of z" range plus prev/next + page nums.*/
/* ------------------------------------------------------------------ */
const TablePagination = ({ currentPage, totalItems, pageSize, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const getPageList = () => {
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    const pages = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  };

  const pageList = getPageList();
  const btnClass = (active) =>
    `${styles.paginationButton} ${active ? styles.paginationButtonActive : ""}`;

  return (
    <div className={styles.tablePagination}>
      <span className={styles.tablePaginationInfo}>
        Showing <strong>{startItem}</strong>–<strong>{endItem}</strong> of{" "}
        <strong>{totalItems}</strong>
      </span>

      <div className={styles.tablePaginationControls}>
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={btnClass(false)}
          aria-label="Previous page"
        >
          <FiChevronLeft /> 
        </button>

        {pageList[0] > 1 && (
          <>
            <button
              type="button"
              onClick={() => onPageChange(1)}
              className={btnClass(currentPage === 1)}
            >
              1
            </button>
            {pageList[0] > 2 && <span className={styles.paginationEllipsis}>…</span>}
          </>
        )}

        {pageList.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            className={btnClass(p === currentPage)}
          >
            {p}
          </button>
        ))}

        {pageList[pageList.length - 1] < totalPages && (
          <>
            {pageList[pageList.length - 1] < totalPages - 1 && (
              <span className={styles.paginationEllipsis}>…</span>
            )}
            <button
              type="button"
              onClick={() => onPageChange(totalPages)}
              className={btnClass(currentPage === totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={btnClass(false)}
          aria-label="Next page"
        >
           <FiChevronRight />
        </button>
      </div>
    </div>
  );
};

// Shimmering stand-in rows for a report table while background sentiment
// enrichment (see sentimentEnriching) is still settling — sentiment-driven
// columns (e.g. Positive %, Sentiment) and row counts (deleted comments get
// dropped once confirmed) can change once enrichment finishes, so this
// holds a placeholder shape instead of showing the pre-enrichment numbers
// and then visibly correcting them a few seconds later.
const SKELETON_TABLE_ROW_COUNT = 5;
const TableSkeletonRows = ({ columnCount, rowCount = SKELETON_TABLE_ROW_COUNT }) => (
  <>
    {Array.from({ length: rowCount }).map((_, rowIndex) => (
      <tr key={rowIndex} aria-hidden="true">
        {Array.from({ length: columnCount }).map((_, colIndex) => (
          <td key={colIndex}>
            <span
              className={`${styles.skeletonBlock} ${styles.skeletonTableCell}`}
              style={{ width: colIndex === 0 ? "70%" : "55%" }}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const InstagramReport = () => {
  const loginData = JSON.parse(localStorage.getItem("login"));
  const userId = loginData?.user?.userId;
  const token = loginData?.token;
  const [activeAccountId, setActiveAccountId] = useState(
    () => localStorage.getItem("instagramActiveAccountId") || "all",
  );
  const scopedAccountId =
    activeAccountId && activeAccountId !== "all" ? activeAccountId : undefined;

  const [connectedAccounts, setConnectedAccounts] = useState(() => {
    try {
      const saved = localStorage.getItem("instagramAccounts");
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  // The instaReport endpoint's acc_filter contract: "all" scopes the
  // report to every connected account, or a specific insta_user_id scopes
  // it to just that account.
  const accountFilterValue = scopedAccountId || "all";

  const handleSelectAccount = (accountId) => {
    setActiveAccountId(accountId);
    localStorage.setItem("instagramActiveAccountId", accountId);
    window.dispatchEvent(
      new CustomEvent("instagram-account-changed", {
        detail: { accountId },
      }),
    );
  };

  useEffect(() => {
    const sync = (accountId) => {
      if (!accountId) return;
      setActiveAccountId(accountId);
    };
    const syncAccounts = (accounts) => {
      if (!Array.isArray(accounts)) return;
      setConnectedAccounts(accounts);
    };
    const onCustom = (e) => sync(e.detail?.accountId);
    const onAccountsCustom = (e) => syncAccounts(e.detail?.accounts);
    const onStorage = (e) => {
      if (e.key === "instagramActiveAccountId" && e.newValue) sync(e.newValue);
      if (e.key === "instagramAccounts") {
        try {
          syncAccounts(e.newValue ? JSON.parse(e.newValue) : []);
        } catch {
          // Ignore malformed storage payloads.
        }
      }
    };
    window.addEventListener("instagram-account-changed", onCustom);
    window.addEventListener("instagram-accounts-changed", onAccountsCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("instagram-account-changed", onCustom);
      window.removeEventListener(
        "instagram-accounts-changed",
        onAccountsCustom,
      );
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Unlike the other Instagram pages, "All Accounts" is a valid, visible
  // choice here — so if the currently selected specific account gets
  // disconnected (from this page or another tab), fall back to "All
  // Accounts" rather than picking a different specific account.
  useEffect(() => {
    if (activeAccountId === "all") return;
    if (connectedAccounts.length === 0) return;

    const stillExists = connectedAccounts.some(
      (account) =>
        String(account.id) === String(activeAccountId) ||
        String(account.instaUserId) === String(activeAccountId),
    );

    if (!stillExists) {
      handleSelectAccount("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId, connectedAccounts]);

  const stickyHeaderRef = useRef(null);
  const [stickyHeaderOffset, setStickyHeaderOffset] = useState(64);
  const [fixedBarHeight, setFixedBarHeight] = useState(64);
  const [fixedBarLeft, setFixedBarLeft] = useState(0);
  const [fixedBarWidth, setFixedBarWidth] = useState("100%");

  useEffect(() => {
    const measure = () => {
      const globalNavbar = document.querySelector(".navbar.dashboard-header");
      if (globalNavbar) {
        const top = Math.max(0, Math.ceil(globalNavbar.getBoundingClientRect().bottom));
        if (top > 0) setStickyHeaderOffset(top);
      }
      const mainEl = document.querySelector(".main");
      if (mainEl) {
        const r = mainEl.getBoundingClientRect();
        setFixedBarLeft(Math.max(0, Math.round(r.left)));
        setFixedBarWidth(`${Math.round(r.width)}px`);
      }
      if (stickyHeaderRef.current) {
        const h = Math.ceil(stickyHeaderRef.current.getBoundingClientRect().height);
        if (h > 0) setFixedBarHeight(h);
      }
    };
    measure();
    const raf = requestAnimationFrame(measure);
    const t = setTimeout(measure, 120);
    const globalNavbar = document.querySelector(".navbar.dashboard-header");
    const mainEl = document.querySelector(".main");
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && globalNavbar) ro.observe(globalNavbar);
    if (ro && mainEl) ro.observe(mainEl);
    if (ro && stickyHeaderRef.current) ro.observe(stickyHeaderRef.current);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [connectedAccounts.length]);

  const handleConnectInstagram = () => {
    const INSTAGRAM_APP_ID = process.env.REACT_APP_INSTAGRAM_APP_ID;
    const INSTAGRAM_PERMISSION = process.env.REACT_APP_INSTAGRAM_PERMISSION;
    const INSTAGRAM_AUTH_ENDPOINT = process.env.REACT_APP_INSTAGRAM_AUTH_ENDPOINT;
    const INSTAGRAM_REDIRECT_URI = process.env.REACT_APP_INSTAGRAM_REDIRECT_URI;
    if (!userId || !INSTAGRAM_APP_ID) return;
    try {
      window.location.assign(
        `${INSTAGRAM_AUTH_ENDPOINT}?force_reauth=true&client_id=${encodeURIComponent(
          INSTAGRAM_APP_ID,
        )}&redirect_uri=${encodeURIComponent(
          INSTAGRAM_REDIRECT_URI,
        )}&response_type=code&scope=${encodeURIComponent(
          INSTAGRAM_PERMISSION,
        )}&state=${encodeURIComponent(userId)}`,
      );
    } catch {
      // ignore
    }
  };

  const today = useMemo(() => toDateInputValue(new Date()), []);

  const [filters, setFilters] = useState({
    dateFrom: today,
    dateTo: today,
    mediaId: "all",
    type: "all",
  });

  // Client-side-only filters (the backend's instaReport method doesn't
  // accept a keyword or sentiment param yet — see the notes shared
  // alongside this update for what the backend would need to add).
  const [keywordFilter, setKeywordFilter] = useState("all");
  const [sentimentFilterValue, setSentimentFilterValue] = useState("all");
  const [logSearchQuery, setLogSearchQuery] = useState("");
  const [chartMode, setChartMode] = useState("stacked");

  // Posts list for the "Post" filter dropdown — id -> {thumbnail, caption}.
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postDropdownOpen, setPostDropdownOpen] = useState(false);
  const postDropdownRef = useRef(null);

  useEffect(() => {
    if (!postDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (
        postDropdownRef.current &&
        !postDropdownRef.current.contains(event.target)
      ) {
        setPostDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [postDropdownOpen]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // True while enrichRowsWithCommentSentiment is filling in real sentiment
  // (and, as a side effect, dropping rows for comments deleted on
  // Instagram since instaReport logged them) for the report that just
  // loaded. The summary cards use this to hold a skeleton in place of
  // their numbers instead of showing the pre-enrichment count and then
  // visibly jumping to the corrected one a few seconds later.
  const [sentimentEnriching, setSentimentEnriching] = useState(false);

  // The backend tags every reply posted by the connected account as
  // comment_source: "auto_reply" — whether it was fired by an automation
  // rule or sent by a person clicking the "Reply" button right here. So
  // once a reply has no matching automation rule (matchLog), the API gives
  // us no reliable way to tell "a rule sent this" from "I sent this" — see
  // linkReplyReportRows in instagramApi.js. The one place we *do* know for
  // certain is the moment this UI sends the reply, so we remember that
  // commentId locally (persisted so it survives refetches/date-filter
  // changes) and force the row's type to "manual" whenever it's replayed
  // back from the API, regardless of what comment_source says.
  const manualReplyStorageKey = "instagramReport:manualReplyCommentIds";
  const [manualReplyCommentIds, setManualReplyCommentIds] = useState(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem(manualReplyStorageKey) || "[]",
      );
      return new Set(Array.isArray(stored) ? stored : []);
    } catch {
      return new Set();
    }
  });

  const rememberManualReply = (commentId) => {
    if (!commentId) return;
    setManualReplyCommentIds((prev) => {
      const next = new Set(prev);
      next.add(String(commentId));
      try {
        localStorage.setItem(
          manualReplyStorageKey,
          JSON.stringify(Array.from(next)),
        );
      } catch {
        // localStorage unavailable (private mode, quota, etc.) — the
        // in-memory Set still keeps this tab correct for the session.
      }
      return next;
    });
  };

  // Inline reply composer state for the log stream table's Reply action.
  const [replyDraftRowId, setReplyDraftRowId] = useState(null);
  const [replyDraftText, setReplyDraftText] = useState("");
  const [sendingReplyRowId, setSendingReplyRowId] = useState(null);

  // Full reply preview modal — the "Reply Sent" cell truncates long
  // replies with an ellipsis so the table stays readable; clicking the
  // truncated text opens this modal with the untruncated comment + reply.
  const [replyPreviewRow, setReplyPreviewRow] = useState(null);
  const handleOpenReplyPreview = (row) => setReplyPreviewRow(row);
  const handleCloseReplyPreview = () => setReplyPreviewRow(null);

  useEffect(() => {
    if (!replyPreviewRow) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setReplyPreviewRow(null);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [replyPreviewRow]);

  // Pagination state — one page counter per table, both reset whenever
  // the underlying filtered data set changes (see effect further down).
  const [keywordTablePage, setKeywordTablePage] = useState(1);
  const [logTablePage, setLogTablePage] = useState(1);

//  auto hide for the status messages
//3.5 secs for success and 4.5 for failed
  useEffect(() => {
    if (!errorMessage) return;

    const timer = setTimeout(() => setErrorMessage(""), 4500);

    return () => clearTimeout(timer);
  }, [errorMessage]);

  const loadPosts = async () => {
    if (!userId) return;

    setPostsLoading(true);
    try {
      const result = await fetchInstagramAccountData({ userId, instaUserId: scopedAccountId });
      const payload = result?.data || result;
      const items = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.data?.data)
            ? payload.data.data
            : [];

      setPosts(
        items
          .filter((item) => item?.id)
          .map((item) => ({
            id: String(item.id),
           thumbnail:
            item.media_type === "VIDEO"
              ? item.thumbnail_url || null
              : item.media_url || null,
            caption: item.caption || "",
            // Optional engagement fields — populate these on the media
            // payload from getInstagramData to light up the "Post
            // Engagement Metrics" summary card below.
            newFollowers: item.new_followers,
            shareCount: item.share_count,
            repostCount: item.repost_count,
            savedCount: item.saved_count,
          })),
      );
    } catch (error) {
      // Non-fatal — the post filter just falls back to "All Posts" only.
    } finally {
      setPostsLoading(false);
    }
  };

  // instaReport rows mostly come back with no sentiment. fetchInstagramComments
  // (per-media) DOES send real sentiment on every comment and reply, so once
  // report rows are in, this fetches comments for whichever posts are
  // missing sentiment and stitches the real values back in by matching
  // comment_id. Cached per mediaId so switching filters doesn't re-fetch.
  //
  // Rows whose commentId isn't found — even though the fetch for that post
  // succeeded — mean the comment no longer exists on Instagram (deleted or
  // edited away). Those rows are dropped from the report entirely rather
  // than shown with a blank sentiment, so the table only ever shows
  // comments that are still actually there.
  const commentSentimentCacheRef = useRef(new Map());

  const getMediaCommentSentimentMap = async (mediaId, instaUserId) => {
    const cacheKey = String(mediaId);
    if (commentSentimentCacheRef.current.has(cacheKey)) {
      return commentSentimentCacheRef.current.get(cacheKey);
    }

    const sentimentMap = new Map();
    let fetchSucceeded = true;
    try {
      const result = await fetchInstagramMediaComments({ userId, mediaId, instaUserId });
      const payload = result?.data || result;
      const mediaItems = Array.isArray(payload) ? payload : payload ? [payload] : [];
      const mediaDetails =
        mediaItems.find((item) => String(item?.id) === cacheKey) || mediaItems[0];

      const topLevelComments = Array.isArray(mediaDetails?.comments)
        ? mediaDetails.comments
        : Array.isArray(mediaDetails?.comments?.data)
          ? mediaDetails.comments.data
          : [];

      const collect = (comment) => {
        if (!comment) return;
        if (comment.id != null && comment.sentiment) {
          sentimentMap.set(String(comment.id), String(comment.sentiment).toLowerCase());
        }
        const replies = Array.isArray(comment.replies)
          ? comment.replies
          : Array.isArray(comment.replies?.data)
            ? comment.replies.data
            : [];
        replies.forEach(collect);
      };

      topLevelComments.forEach(collect);
    } catch (error) {
      // The request itself failed (network/auth/etc) — we genuinely don't
      // know whether these comments still exist, so don't treat any of
      // this media's rows as deleted.
      fetchSucceeded = false;
    }

    const result = { sentimentMap, fetchSucceeded };
    commentSentimentCacheRef.current.set(cacheKey, result);
    return result;
  };

  const enrichRowsWithCommentSentiment = async (fetchedRows) => {
    const rowsMissingSentiment = fetchedRows.filter(
      (row) => !row.sentiment && row.mediaId && row.commentId,
    );
    if (!rowsMissingSentiment.length) return;

    setSentimentEnriching(true);

    const missingMediaIds = Array.from(
      new Set(rowsMissingSentiment.map((row) => String(row.mediaId))),
    );

    const entries = await Promise.all(
      missingMediaIds.map(async (mediaId) => {
        const rowForMedia = rowsMissingSentiment.find(
          (row) => String(row.mediaId) === mediaId,
        );
        const mediaResult = await getMediaCommentSentimentMap(
          mediaId,
          scopedAccountId || rowForMedia?.accountId || undefined,
        );
        return [mediaId, mediaResult];
      }),
    );
    const resultByMediaId = new Map(entries);

    let deletedCount = 0;
    const deletedSample = [];

   setRows((prevRows) => {
  const updatedRows = prevRows.reduce((kept, row) => {
    if (row.sentiment || !row.mediaId || !row.commentId) {
      kept.push(row);
      return kept;
    }

    const mediaResult = resultByMediaId.get(String(row.mediaId));

    if (!mediaResult || !mediaResult.fetchSucceeded) {
      kept.push(row);
      return kept;
    }

    const sentiment = mediaResult.sentimentMap.get(
      String(row.commentId)
    );

    if (sentiment) {
      kept.push({
        ...row,
        sentiment,
      });
      return kept;
    }

    deletedCount += 1;
    return kept;
  }, []);

  setTotal(updatedRows.length);

  return updatedRows;
});

    setTotal((prevTotal) => Math.max(0, prevTotal - deletedCount));

    // Diagnostic only — confirms *why* rows were removed: their comment_id
    // wasn't found in fetchInstagramComments for that post even though the
    // fetch succeeded, meaning the comment no longer exists on Instagram.
    if (deletedCount > 0) {
      console.info(
        `[InstagramReport] Removed ${deletedCount} row(s) whose comment no longer ` +
          `exists on Instagram (comment_id not found in a successful ` +
          `fetchInstagramComments response). Sample:`,
        deletedSample,
      );
    }

    setSentimentEnriching(false);
  };

  const loadReports = async () => {
    if (!userId || !token) return;

    setErrorMessage("");
    setRowsLoading(true);
    setSentimentEnriching(false);
    try {
      const result = await fetchInstagramReplyReports({
        userId,
        token,
        mediaId: filters.mediaId !== "all" ? filters.mediaId : undefined,
        replyType: filters.type !== "all" ? filters.type : undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        instaUserId: accountFilterValue,
      });

      const fetchedRows = result?.data || [];
      // Override anything the API/merge logic classified as "automatic"
      // when we know for a fact this row's reply was sent manually from
      // this Reply button — see manualReplyCommentIds above.
      fetchedRows.forEach((row) => {
        if (row.commentId && manualReplyCommentIds.has(String(row.commentId))) {
          row.type = "manual";
        }
      });
      setRows(fetchedRows);
      setTotal(result?.total ?? fetchedRows.length);

      // Fire-and-forget: fills in real sentiment (from fetchInstagramComments)
      // for whichever rows instaReport didn't send it for, once it's ready.
      enrichRowsWithCommentSentiment(fetchedRows);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load the reply report.");
      setRows([]);
      setTotal(0);
    } finally {
      setRowsLoading(false);
    }
  };

  
  useEffect(() => {
    setFilters((prev) =>
      prev.mediaId === "all" ? prev : { ...prev, mediaId: "all" },
    );
    setReplyDraftRowId(null);
    setReplyDraftText("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId]);

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId]);

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeAccountId]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({ dateFrom: today, dateTo: today, mediaId: "all", type: "all" });
    setKeywordFilter("all");
    setSentimentFilterValue("all");
    setLogSearchQuery("");
  };

  const handleQuickRange = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    setFilters((prev) => ({
      ...prev,
      dateFrom: toDateInputValue(from),
      dateTo: toDateInputValue(to),
    }));
  };

  const getPostThumb = (mediaId) => {
    if (!mediaId) return null;
    return posts.find((post) => post.id === String(mediaId))?.thumbnail || null;
  };

  const getPostLabel = (post, index) => {
    if (post.caption) {
      return post.caption.length > 40
        ? `${post.caption.slice(0, 40)}…`
        : post.caption;
    }
    return `Post ${index + 1}`;
  };

  const selectedPostIndex = posts.findIndex((post) => post.id === filters.mediaId);
  const selectedPost =
    filters.mediaId !== "all" && selectedPostIndex !== -1
      ? posts[selectedPostIndex]
      : null;

  const handleSelectPost = (mediaId) => {
    handleFilterChange("mediaId", mediaId);
    setPostDropdownOpen(false);
  };


  const formatReportDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // row.sentiment is backend-supplied when instaReport sends it, and
  // otherwise backfilled after load from real per-comment sentiment
  // returned by fetchInstagramComments (see enrichRowsWithCommentSentiment
  // above) — matched by comment_id, not guessed from the text.
  // hasSentimentData stays as a safety net for rows that still end up with
  // no sentiment either way (e.g. a comment that's since been deleted).
  const getRowSentiment = (row) =>
    row?.sentiment ? String(row.sentiment).toLowerCase() : null;

  const hasSentimentData = useMemo(
    () => rows.some((row) => getRowSentiment(row)),
    [rows],
  );

  const availableKeywords = useMemo(
    () =>
      Array.from(new Set(rows.map((row) => row.matchedKeyword).filter(Boolean))),
    [rows],
  );

  // Keyword + sentiment filters aren't supported by the instaReport method
  // yet, so they're applied here on the rows already returned for the
  // current date/post/type filters.
  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        if (keywordFilter !== "all" && row.matchedKeyword !== keywordFilter) {
          return false;
        }
        if (
          sentimentFilterValue !== "all" &&
          getRowSentiment(row) !== sentimentFilterValue
        ) {
          return false;
        }
        return true;
      }),
    [rows, keywordFilter, sentimentFilterValue],
  );

  const visibleLogRows = useMemo(() => {
    const query = logSearchQuery.trim().toLowerCase();
    const base = query
      ? filteredRows.filter((row) => {
          const comment = (row.originalComment || "").toLowerCase();
          const user = (row.recipientName || "").toLowerCase();
          return comment.includes(query) || user.includes(query);
        })
      : filteredRows;

    // Most recent comment first.
    return [...base].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [filteredRows, logSearchQuery]);

  // ---- Summary cards (all computed from the rows already on screen) ----

  // Uses the backend's own total when no client-side-only filter is
  // active (accurate even if the API paginates); falls back to a direct
  // count once the keyword/sentiment filters (which the API doesn't know
  // about) are narrowing things down.
  // const totalCommentsCount =
  //   keywordFilter === "all" && sentimentFilterValue === "all"
  //     ? total
  //     : filteredRows.length;
  const totalCommentsCount = filteredRows.length;

  const savedKeywordRows = filteredRows.filter((row) => row.matchedKeyword);
  const savedKeywordCount = savedKeywordRows.length;
  const savedKeywordPercent = totalCommentsCount
    ? Math.round((savedKeywordCount / totalCommentsCount) * 100)
    : 0;
  const topTrackedKeywords = Array.from(
    new Set(savedKeywordRows.map((row) => row.matchedKeyword)),
  ).slice(0, 4);

  const repliedKeywordRows = savedKeywordRows.filter((row) =>
    (row.reply || "").trim(),
  );
  const repliesOverKeywordCount = repliedKeywordRows.length;
  const repliesOverKeywordAuto = repliedKeywordRows.filter(
    (row) => row.type === "automatic",
  ).length;
  const repliesOverKeywordManual = repliedKeywordRows.filter(
    (row) => row.type === "manual",
  ).length;
  const keywordCoveragePercent = savedKeywordCount
    ? Math.round((repliesOverKeywordCount / savedKeywordCount) * 100)
    : 0;

  const positiveCount = filteredRows.filter(
    (row) => getRowSentiment(row) === "positive",
  ).length;
  const positiveRatio =
    hasSentimentData && filteredRows.length
      ? Math.round((positiveCount / filteredRows.length) * 100)
      : null;

  // Post engagement metrics — only renders once at least one loaded post
  // carries these optional fields (see loadPosts above).
  const engagementTotals = useMemo(
    () =>
      posts.reduce(
        (acc, post) => {
          if (post.newFollowers !== undefined)
            acc.follows += Number(post.newFollowers) || 0;
          if (post.shareCount !== undefined)
            acc.shares += Number(post.shareCount) || 0;
          if (post.repostCount !== undefined)
            acc.reposts += Number(post.repostCount) || 0;
          if (post.savedCount !== undefined)
            acc.saves += Number(post.savedCount) || 0;
          return acc;
        },
        { follows: 0, shares: 0, reposts: 0, saves: 0 },
      ),
    [posts],
  );

  const hasEngagementData = posts.some(
    (post) =>
      post.newFollowers !== undefined ||
      post.shareCount !== undefined ||
      post.repostCount !== undefined ||
      post.savedCount !== undefined,
  );

  // ---- Daywise sentiment/comment trend chart ----
  const dailyData = useMemo(() => {
    const byDay = new Map();

    filteredRows.forEach((row) => {
      const date = new Date(row.date);
      if (Number.isNaN(date.getTime())) return;

      const key = date.toISOString().slice(0, 10);
      if (!byDay.has(key)) {
        byDay.set(key, {
          date: key,
          total: 0,
          positive: 0,
          neutral: 0,
          negative: 0,
        });
      }

      const bucket = byDay.get(key);
      bucket.total += 1;

      const sentiment = getRowSentiment(row);
      if (sentiment === "positive") bucket.positive += 1;
      else if (sentiment === "neutral") bucket.neutral += 1;
      else if (sentiment === "negative") bucket.negative += 1;
    });

    return Array.from(byDay.values()).sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );
  }, [filteredRows]);

  // ---- Saved Keywords ORM Analysis table ----
  const keywordStats = useMemo(() => {
    const byKeyword = new Map();

    filteredRows.forEach((row) => {
      if (!row.matchedKeyword) return;

      if (!byKeyword.has(row.matchedKeyword)) {
        byKeyword.set(row.matchedKeyword, {
          keyword: row.matchedKeyword,
          matches: 0,
          auto: 0,
          manual: 0,
          positive: 0,
          sentimentSampled: 0,
        });
      }

      const bucket = byKeyword.get(row.matchedKeyword);
      bucket.matches += 1;

      if ((row.reply || "").trim()) {
        if (row.type === "automatic") bucket.auto += 1;
        else bucket.manual += 1;
      }

      const sentiment = getRowSentiment(row);
      if (sentiment) {
        bucket.sentimentSampled += 1;
        if (sentiment === "positive") bucket.positive += 1;
      }
    });

    return Array.from(byKeyword.values()).sort((a, b) => b.matches - a.matches);
  }, [filteredRows]);

  // Reset both tables back to page 1 whenever the underlying data set
  // changes (new filters, new search query, account switch, etc.) so the
  // user never lands on a now-empty page.
  useEffect(() => {
    setKeywordTablePage(1);
  }, [keywordStats.length, filters, activeAccountId, keywordFilter, sentimentFilterValue]);

  useEffect(() => {
    setLogTablePage(1);
  }, [visibleLogRows.length, filters, activeAccountId, logSearchQuery, keywordFilter, sentimentFilterValue]);

  const paginatedKeywordStats = useMemo(() => {
    const start = (keywordTablePage - 1) * TABLE_PAGE_SIZE;
    return keywordStats.slice(start, start + TABLE_PAGE_SIZE);
  }, [keywordStats, keywordTablePage]);

  const paginatedLogRows = useMemo(() => {
    const start = (logTablePage - 1) * TABLE_PAGE_SIZE;
    return visibleLogRows.slice(start, start + TABLE_PAGE_SIZE);
  }, [visibleLogRows, logTablePage]);

  // ---- CSV export — fully client-side from the rows already loaded ----
  const handleExportCsv = () => {
    const headers = [
      "Date",
      "Type",
      "User",
      "Post ID",
      "Keyword",
      "Sentiment",
      "Reply",
      "Comment",
    ];

    const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

    const csvRows = filteredRows.map((row) => [
      formatReportDate(row.date),
      row.type || "",
      row.recipientName || "",
      row.mediaId || "",
      row.matchedKeyword || "",
      getRowSentiment(row) || "",
      row.reply || "",
      row.originalComment || "",
    ]);

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `instagram-report-${filters.dateFrom}-to-${filters.dateTo}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---- Inline reply from the log stream table ----
  // Only usable once a row carries a real comment ID from the backend —
  // see the commentId note added to normalizeReplyReportRow in
  // instagramApi.js. Until then the Reply button stays disabled.
  const handleOpenReplyDraft = (row) => {
    setReplyDraftRowId(row.id);
    setReplyDraftText("");
  };

  const handleSendLogReply = async (row) => {
    if (!row.commentId || !replyDraftText.trim()) return;

    setSendingReplyRowId(row.id);
    try {
      await replyToComment({
        userId,
        commentId: row.commentId,
        mediaId: row.mediaId,
        message: replyDraftText.trim(),
        commentText: row.originalComment,
        instaUserId: scopedAccountId || row.accountId || undefined,
      });
      rememberManualReply(row.commentId);
      setReplyDraftRowId(null);
      setReplyDraftText("");
      loadReports();
    } catch (error) {
      setErrorMessage(error.message || "Unable to send reply.");
    } finally {
      setSendingReplyRowId(null);
    }
  };

  const handleCorrectReplyType = (row) => {
    if (!row.commentId) return;
    // Manual correction for rows tagged "Auto" from before this session
    // started remembering manual sends (or from a different browser),
    // where the backend's comment_source can't tell us who actually sent
    // the reply. Persists the same way a fresh manual send does, and
    // updates this row's badge immediately without waiting on a refetch.
    rememberManualReply(row.commentId);
    setRows((prev) =>
      prev.map((r) =>
        r.commentId && String(r.commentId) === String(row.commentId)
          ? { ...r, type: "manual" }
          : r,
      ),
    );
  };

  return (
    <DashboardLayout>
      <main className="content">
      <div className={styles.instagramPage}>
        <div
          ref={stickyHeaderRef}
          className={styles.fixedPageHeader}
          style={{
            top: stickyHeaderOffset,
            left: fixedBarLeft,
            width: fixedBarWidth,
          }}
        >
          <div className={styles.fixedPageHeaderInner}>
            <div className={styles.pageHeaderLeft}>
              <Link
                to="/dashboard/instagram"
                className={styles.backButtonCompact}
                aria-label="Back to Instagram"
              >
                <FiArrowLeft />
              </Link>
              <div className={styles.pageHeaderIcon}>
                <img src="/img/instagram.png" alt="Instagram" />
              </div>
              <div className={styles.pageHeaderTitleRow}>
                <h1>Instagram ORM Report</h1>
              </div>
            </div>
            <div className={styles.pageHeaderRight}>
              <div className={styles.accountPillScroller}>
                <button
                  type="button"
                  className={`${styles.accountPill} ${styles.accountPillAll} ${
                    activeAccountId === "all" ? styles.accountPillActive : ""
                  }`}
                  onClick={() => handleSelectAccount("all")}
                >
                  <FiLayers />
                  All Accounts ({connectedAccounts.length})
                </button>
                {connectedAccounts.map((account) => (
                  <button
                    type="button"
                    key={account.id}
                    className={`${styles.accountPill} ${
                      activeAccountId === account.id
                        ? styles.accountPillActive
                        : ""
                    }`}
                    onClick={() => handleSelectAccount(account.id)}
                  >
                    {account.avatarUrl ? (
                      <img
                        src={account.avatarUrl}
                        alt=""
                        className={styles.accountPillAvatar}
                      />
                    ) : (
                      <span className={styles.accountPillAvatarFallback}>
                        {account.username?.[0]?.toUpperCase() || "?"}
                      </span>
                    )}
                    @{account.username}
                    <span className={styles.accountPillStatusDot} />
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={styles.accountSwitcherConnectButton}
                onClick={handleConnectInstagram}
              >
                <FiPlus />
                Connect IG Account
              </button>
            </div>
          </div>
        </div>
        <div
          className={styles.fixedPageHeaderSpacer}
          style={{ height: fixedBarHeight }}
          aria-hidden="true"
        />

        {errorMessage && (
          <div className={styles.errorMessage}>{errorMessage}</div>
        )}

        {/* ---- Filters ---- */}
        <section className={styles.dataCard}>
          <div className={styles.reportFilterBarHeader}>
            <span className={styles.reportFilterTitle}>
              <FiFilter /> Report Filters
            </span>

            <div className={styles.reportQuickActions}>
              <button
                type="button"
                className={styles.dateRangePill}
                onClick={() => handleQuickRange(7)}
              >
                Last 7 Days
              </button>

              <button
                type="button"
                className={styles.dateRangePill}
                onClick={() => handleQuickRange(14)}
              >
                Last 14 Days
              </button>

              <button
                type="button"
                className={styles.reportResetButton}
                onClick={handleResetFilters}
              >
                <FiRotateCcw /> Reset Filters
              </button>
            </div>
          </div>

          <div className={styles.reportFilterBar}>
            <div className={styles.reportFilterField}>
              <label>Account</label>
              <select
                value={activeAccountId}
                onChange={(event) => handleSelectAccount(event.target.value)}
                className={styles.reportFilterInput}
                disabled={!connectedAccounts.length}
              >
                <option value="all">All Accounts</option>
                {connectedAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    @{account.username}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.reportFilterField}>
              <label>From</label>
              <input
                type="date"
                value={filters.dateFrom}
                max={filters.dateTo || today}
                onChange={(event) =>
                  handleFilterChange("dateFrom", event.target.value)
                }
                className={styles.reportFilterInput}
              />
            </div>

            <div className={styles.reportFilterField}>
              <label>To</label>
              <input
                type="date"
                value={filters.dateTo}
                min={filters.dateFrom}
                max={today}
                onChange={(event) =>
                  handleFilterChange("dateTo", event.target.value)
                }
                className={styles.reportFilterInput}
              />
            </div>

            <div
              className={styles.reportFilterField}
              ref={postDropdownRef}
              style={{ position: "relative" }}
            >
              <label>Post</label>
              <button
                type="button"
                className={styles.reportPostDropdownTrigger}
                onClick={() => setPostDropdownOpen((open) => !open)}
                disabled={postsLoading}
              >
                <span className={styles.reportPostDropdownTriggerLabel}>
                  {selectedPost ? (
                    <>
                      {selectedPost.thumbnail ? (
                        <img
                          src={selectedPost.thumbnail}
                          alt=""
                          className={styles.reportPostDropdownThumb}
                        />
                      ) : (
                        <span className={styles.reportPostDropdownThumbFallback}>
                          <FiImage />
                        </span>
                      )}
                      {getPostLabel(selectedPost, selectedPostIndex)}
                    </>
                  ) : postsLoading ? (
                    "Loading posts..."
                  ) : (
                    "All Instagram Posts"
                  )}
                </span>
                <FiChevronDown className={styles.reportPostDropdownChevron} />
              </button>

              {postDropdownOpen && (
                <div className={styles.reportPostDropdownPanel}>
                  <button
                    type="button"
                    className={`${styles.reportPostDropdownItem} ${
                      filters.mediaId === "all"
                        ? styles.reportPostDropdownItemActive
                        : ""
                    }`}
                    onClick={() => handleSelectPost("all")}
                  >
                    All Instagram Posts
                  </button>

                  {posts.map((post, index) => (
                    <button
                      key={post.id}
                      type="button"
                      className={`${styles.reportPostDropdownItem} ${
                        filters.mediaId === post.id
                          ? styles.reportPostDropdownItemActive
                          : ""
                      }`}
                      onClick={() => handleSelectPost(post.id)}
                    >
                      {post.thumbnail ? (
                        <img
                          src={post.thumbnail}
                          alt=""
                          className={styles.reportPostDropdownThumb}
                        />
                      ) : (
                        <span className={styles.reportPostDropdownThumbFallback}>
                          <FiImage />
                        </span>
                      )}
                      <span className={styles.reportPostDropdownItemLabel}>
                        {getPostLabel(post, index)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.reportFilterField}>
              <label>Saved Keyword</label>
              <select
                value={keywordFilter}
                onChange={(event) => setKeywordFilter(event.target.value)}
                className={styles.reportFilterInput}
                disabled={!availableKeywords.length}
              >
                <option value="all">All Keywords</option>
                {availableKeywords.map((keyword) => (
                  <option key={keyword} value={keyword}>
                    #{keyword}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.reportFilterField}>
              <label>Sentiment</label>
              <select
                value={sentimentFilterValue}
                onChange={(event) => setSentimentFilterValue(event.target.value)}
                className={styles.reportFilterInput}
                disabled={!hasSentimentData}
              >
                <option value="all">All Sentiments</option>
                <option value="positive">Positive</option>
                <option value="neutral">Neutral</option>
                <option value="negative">Negative</option>
              </select>
            </div>

            <div className={styles.reportFilterField}>
              <label>Type</label>
              <select
                value={filters.type}
                onChange={(event) =>
                  handleFilterChange("type", event.target.value)
                }
                className={styles.reportFilterInput}
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* ---- Summary stat cards ----
             .statCardsGrid already switches from auto-fit(230px) → auto-fit(200px)
             → single column at the 1024px/768px breakpoints defined in the CSS
             module, so no inline grid override is used here — an inline style
             would win over those @media rules and break the responsiveness. */}
        <div className={styles.statCardsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statCardHeader}>
              <span>Total Comments</span>
              <FiMessageCircle />
            </div>
            <div className={styles.statCardValue}>
              {sentimentEnriching ? (
                <span
                  className={`${styles.skeletonBlock} ${styles.skeletonStatValue}`}
                  aria-label="Updating comment count…"
                />
              ) : (
                totalCommentsCount.toLocaleString()
              )}
            </div>
            {positiveRatio !== null && (
              <div className={styles.statCardFooter}>
                <span className={styles.statCardMuted}>Positive Ratio</span>
                <span className={styles.statCardHighlightGreen}>
                  {positiveRatio}%
                </span>
              </div>
            )}
          </div>

          <div className={`${styles.statCard} ${styles.statCardAccent}`}>
            <div className={styles.statCardHeader}>
              <span>Saved Keyword Comments</span>
              <FiTag />
            </div>
            <div className={styles.statCardValue}>
              {sentimentEnriching ? (
                <span
                  className={`${styles.skeletonBlock} ${styles.skeletonStatValue}`}
                  aria-label="Updating saved keyword count…"
                />
              ) : (
                savedKeywordCount.toLocaleString()
              )}
            </div>
            <div className={styles.statCardFooter}>
              <span className={styles.statCardMuted}>
                {savedKeywordPercent}% of total
              </span>
            </div>
            {topTrackedKeywords.length > 0 && (
              <div className={styles.statCardTags}>
                Tracked Keywords:{" "}
                {topTrackedKeywords.map((keyword) => (
                  <span key={keyword} className={styles.keywordTag}>
                    #{keyword}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className={`${styles.statCard} ${styles.statCardMint}`}>
            <div className={styles.statCardHeader}>
              <span>Replies Done Over Keyword</span>
              <FiZap />
            </div>
            <div className={styles.statCardValue}>
              {sentimentEnriching ? (
                <span
                  className={`${styles.skeletonBlock} ${styles.skeletonStatValue}`}
                  aria-label="Updating reply count…"
                />
              ) : (
                repliesOverKeywordCount.toLocaleString()
              )}
            </div>
            <div className={styles.statCardFooter}>
              <span className={styles.statCardMuted}>
                {keywordCoveragePercent}% Covered
              </span>
            </div>
            <div className={styles.statCardSplit}>
              <span>
                <FiZap /> Auto: {repliesOverKeywordAuto}
              </span>
              <span>
                <FiUser /> Manual: {repliesOverKeywordManual}
              </span>
            </div>
          </div>

          {hasEngagementData && (
            <div className={styles.statCard}>
              <div className={styles.statCardHeader}>
                <span>Post Engagement Metrics</span>
                <FiBarChart2 />
              </div>
              <div className={styles.engagementGrid}>
                <span>
                  <FiUsers /> Follows: +{engagementTotals.follows.toLocaleString()}
                </span>
                <span>
                  <FiShare2 /> Shares: {engagementTotals.shares.toLocaleString()}
                </span>
                <span>
                  <FiRepeat /> Reposts: {engagementTotals.reposts.toLocaleString()}
                </span>
                <span>
                  <FiBookmark /> Saves: {engagementTotals.saves.toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ---- Daywise trend chart ---- */}
        <section className={styles.dataCard}>
          <div className={styles.chartHeader}>
            <div>
              <h2>
                Daywise Sentiment &amp; Comment Trend Chart{" "}
                {filters.dateFrom && filters.dateTo && (
                  <span className={styles.chartDateRange}>
                    ({filters.dateFrom} to {filters.dateTo})
                  </span>
                )}
              </h2>
              <p>
                Daily volume of {hasSentimentData ? "Positive, Negative, and Neutral " : ""}
                comments{hasSentimentData ? " " : " and replies "}
                {hasSentimentData
                  ? "along with automated keyword replies."
                  : "logged for the selected filters."}
                {" "}
                {chartMode === "area"
                  ? "Hover the line to see the exact count."
                  : "Hover a bar to see the exact count."}
              </p>
            </div>

            <div className={styles.chartModeToggle}>
              <button
                type="button"
                className={`${styles.chartModeButton} ${
                  chartMode === "stacked" ? styles.chartModeButtonActive : ""
                }`}
                onClick={() => setChartMode("stacked")}
              >
                Stacked Bar
              </button>
              <button
                type="button"
                className={`${styles.chartModeButton} ${
                  chartMode === "area" ? styles.chartModeButtonActive : ""
                }`}
                onClick={() => setChartMode("area")}
              >
                Area Trend
              </button>
            </div>
          </div>

          <div className={styles.chartScrollArea}>
            {/* While background sentiment enrichment is running, hold a
                shimmering placeholder here instead of the pre-enrichment
                chart — otherwise the stacked-bar view visibly flips from
                a single purple total to sentiment-colored segments (and
                the Area Trend curve's shape can shift) once real
                sentiment finishes settling in a few seconds later. */}
            {sentimentEnriching ? (
              <ChartSkeleton />
            ) : (
              <DaywiseChart
                data={dailyData}
                mode={chartMode}
                hasSentiment={hasSentimentData}
              />
            )}
          </div>

          <div className={styles.chartLegend}>
            {chartMode === "area" && hasSentimentData ? (
              <>
                <span>
                  <i className={styles.legendDotNegative} /> Negative Comments{" "}
                  <span className={styles.legendEmoji} aria-hidden="true">
                    😠
                  </span>
                </span>
                <span>
                  <i className={styles.legendDotPositive} /> Positive Comments{" "}
                  <span className={styles.legendEmoji} aria-hidden="true">
                    😊
                  </span>
                </span>
              </>
            ) : hasSentimentData ? (
              <>
                <span>
                  <i className={styles.legendDotPositive} /> Positive Comments
                </span>
                <span>
                  <i className={styles.legendDotNeutral} /> Neutral Comments
                </span>
                <span>
                  <i className={styles.legendDotNegative} /> Negative Comments
                </span>
              </>
            ) : (
              <span>
                <i className={styles.legendDotTotal} /> Comments &amp; Replies
              </span>
            )}
          </div>
        </section>

        {/* ---- Saved Keywords ORM Analysis ---- */}
        <section className={styles.dataCard}>
          <div className={styles.reportSectionHeading}>
            <FiTag />
            <div>
              <h2>Saved Keywords ORM Analysis</h2>
              <p>
                Breakdown of user comments triggering saved keywords and
                response rate.
              </p>
            </div>
          </div>

          <div className={styles.reportTableWrapper}>
            {sentimentEnriching ? (
              <table className={styles.reportTable}>
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>Comment Matches</th>
                    <th>Auto Replied</th>
                    <th>Manual Replied</th>
                    <th>Positive %</th>
                  </tr>
                </thead>
                <tbody>
                  <TableSkeletonRows columnCount={5} />
                </tbody>
              </table>
            ) : keywordStats.length === 0 ? (
              <p className={styles.commentStatus}>
                No keyword-matched comments in the selected range.
              </p>
            ) : (
              <table className={styles.reportTable}>
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>Comment Matches</th>
                    <th>Auto Replied</th>
                    <th>Manual Replied</th>
                    <th>Positive %</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedKeywordStats.map((stat) => {
                    const positivePercent = stat.sentimentSampled
                      ? Math.round((stat.positive / stat.sentimentSampled) * 100)
                      : null;

                    return (
                      <tr key={stat.keyword}>
                        <td data-label="Keyword">
                          <span className={styles.keywordTag}>
                            #{stat.keyword}
                          </span>
                        </td>
                        <td data-label="Comment Matches">{stat.matches}</td>
                        <td data-label="Auto Replied">
                          <span className={styles.keywordStatValue}>
                            <FiZap /> {stat.auto}
                          </span>
                        </td>
                        <td data-label="Manual Replied">
                          <span className={styles.keywordStatValue}>
                            <FiUser /> {stat.manual}
                          </span>
                        </td>
                        <td data-label="Positive %">
                          {positivePercent !== null ? (
                            <div className={styles.keywordPositiveBar}>
                              <div className={styles.keywordPositiveTrack}>
                                <div
                                  className={styles.keywordPositiveFill}
                                  style={{ width: `${positivePercent}%` }}
                                />
                              </div>
                              <span>{positivePercent}%</span>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {!sentimentEnriching && (
            <TablePagination
              currentPage={keywordTablePage}
              totalItems={keywordStats.length}
              pageSize={TABLE_PAGE_SIZE}
              onPageChange={setKeywordTablePage}
            />
          )}
        </section>

        {/* ---- Daywise comments & reply log stream ---- */}
        <section className={styles.dataCard}>
          <div className={styles.reportFilterBarHeader}>
            <div className={styles.reportSectionHeading}>
              <FiMessageCircle />
              <div>
                <h2>
                  Daywise Comments &amp; Reply Log Stream{" "}
                  <span className={styles.commentsCountPill}>
                    {sentimentEnriching ? (
                      <span
                        className={`${styles.skeletonBlock} ${styles.skeletonPillCount}`}
                        aria-label="Updating record count…"
                      />
                    ) : (
                      `${visibleLogRows.length} Records`
                    )}
                  </span>
                </h2>
                <p>
                  Detailed log of user comments, detected sentiment, triggered
                  keywords, and auto/manual responses.
                </p>
              </div>
            </div>

            <div className={styles.dmSearchField}>
              <FiSearch />
              <input
                type="text"
                placeholder="Search comments, users..."
                value={logSearchQuery}
                onChange={(event) => setLogSearchQuery(event.target.value)}
              />
            </div>
          </div>

          <div className={styles.reportTableWrapper}>
            {rowsLoading ? (
              <p className={styles.commentStatus}>Loading report...</p>
            ) : sentimentEnriching ? (
              <table className={styles.reportTable}>
                <colgroup>
                  <col className={styles.reportColDate} />
                  <col className={styles.reportColUser} />
                  <col className={styles.reportColPost} />
                  <col className={styles.reportColComment} />
                  <col className={styles.reportColSentiment} />
                  <col className={styles.reportColKeyword} />
                  <col className={styles.reportColReply} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Date &amp; Time</th>
                    <th>User</th>
                    <th>Post</th>
                    <th>Comment</th>
                    <th>Sentiment</th>
                    <th>Keyword</th>
                    <th>Reply Sent</th>
                  </tr>
                </thead>
                <tbody>
                  <TableSkeletonRows columnCount={7} />
                </tbody>
              </table>
            ) : visibleLogRows.length === 0 ? (
              <p className={styles.commentStatus}>
                No replies found for the selected filters.
              </p>
            ) : (
              <table className={styles.reportTable}>
                <colgroup>
                  <col className={styles.reportColDate} />
                  <col className={styles.reportColUser} />
                  <col className={styles.reportColPost} />
                  <col className={styles.reportColComment} />
                  <col className={styles.reportColSentiment} />
                  <col className={styles.reportColKeyword} />
                  <col className={styles.reportColReply} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Date &amp; Time</th>
                    <th>User</th>
                    <th>Post</th>
                    <th>Comment</th>
                    <th>Sentiment</th>
                    <th>Keyword</th>
                    <th>Reply Sent</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedLogRows.map((row) => {
                    const thumb = row.mediaThumbnail || getPostThumb(row.mediaId);
                    const sentiment = getRowSentiment(row);
                    const hasReply = Boolean((row.reply || "").trim());

                    return (
                      <React.Fragment key={row.id}>
                        <tr>
                          <td data-label="Date & Time">
                            {formatReportDate(row.date)}
                          </td>

                          <td data-label="User">
                            <div className={styles.logUserCell}>
                              <span className={styles.logUserAvatar}>
                                {(row.recipientName || "?")
                                  .trim()
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                              {row.recipientName || "Instagram user"}
                            </div>
                          </td>

                          <td data-label="Post">
                            {thumb ? (
                              <div className={styles.logPostCell}>
                                <img
                                  src={thumb}
                                  alt="Post"
                                  className={styles.reportPostThumb}
                                />
                                {row.mediaId && (
                                  <span className={styles.logPostId}>
                                    {row.mediaId}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className={styles.reportPostThumbFallback}>
                                No image
                              </span>
                            )}
                          </td>

                          <td data-label="Comment" className={styles.reportTextCell}>
                            {row.originalComment
                              ? `"${row.originalComment}"`
                              : "—"}
                          </td>

                          <td data-label="Sentiment">
                            {sentiment ? (
                              <span
                                className={`${styles.sentimentPill} ${
                                  styles[
                                    `sentimentPill${
                                      sentiment.charAt(0).toUpperCase() +
                                      sentiment.slice(1)
                                    }`
                                  ] || ""
                                }`}
                              >
                                {sentiment.charAt(0).toUpperCase() +
                                  sentiment.slice(1)}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>

                          <td data-label="Keyword">
                            {row.matchedKeyword ? (
                              <span className={styles.keywordTag}>
                                #{row.matchedKeyword}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>

                          <td data-label="Reply Sent">
                            {hasReply ? (
                              <div className={styles.logReplyCell}>
                                <button
                                  type="button"
                                  className={`${styles.reportTypeBadge} ${
                                    row.type === "automatic"
                                      ? styles.reportTypeAuto
                                      : styles.reportTypeManual
                                  }`}
                                  onClick={() =>
                                    row.type === "automatic" &&
                                    handleCorrectReplyType(row)
                                  }
                                  disabled={row.type !== "automatic"}
                                  style={{
                                    border: "none",
                                    font: "inherit",
                                    cursor:
                                      row.type === "automatic"
                                        ? "pointer"
                                        : "default",
                                  }}
                                  title={
                                    row.type === "automatic"
                                      ? "Sent this one yourself? Click to mark it Manual."
                                      : undefined
                                  }
                                >
                                  {row.type === "automatic" ? "Auto" : "Manual"}
                                </button>
                                <button
                                  type="button"
                                  className={styles.logReplyTextButton}
                                  onClick={() => handleOpenReplyPreview(row)}
                                  title="Click to view full reply"
                                >
                                  {row.reply}
                                </button>
                              </div>
                            ) : (
                              <span className={styles.logUnreplied}>
                                Unreplied
                              </span>
                            )}
                          </td>
                        </tr>

                        {replyDraftRowId === row.id && (
                          <tr className={styles.logReplyDraftRow}>
                            <td colSpan={7}>
                              <div className={styles.logReplyDraftBox}>
                                <input
                                  type="text"
                                  value={replyDraftText}
                                  onChange={(event) =>
                                    setReplyDraftText(event.target.value)
                                  }
                                  placeholder={`Reply to ${
                                    row.recipientName || "this comment"
                                  }...`}
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  className={styles.logReplySendButton}
                                  onClick={() => handleSendLogReply(row)}
                                  disabled={
                                    sendingReplyRowId === row.id ||
                                    !replyDraftText.trim()
                                  }
                                >
                                  {sendingReplyRowId === row.id
                                    ? "Sending..."
                                    : "Send"}
                                </button>
                                <button
                                  type="button"
                                  className={styles.logReplyCancelButton}
                                  onClick={() => setReplyDraftRowId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {!rowsLoading && !sentimentEnriching && visibleLogRows.length > 0 && (
            <TablePagination
              currentPage={logTablePage}
              totalItems={visibleLogRows.length}
              pageSize={TABLE_PAGE_SIZE}
              onPageChange={setLogTablePage}
            />
          )}
        </section>
      </div>
      </main>

      {replyPreviewRow && (
        <div className={styles.replyContentOverlay} role="dialog" aria-modal="true">
          <button
            type="button"
            className={styles.replyContentBackdrop}
            aria-label="Close"
            onClick={handleCloseReplyPreview}
          />
          <div className={styles.replyContentModal}>
            <div className={styles.replyContentModalHeader}>
              <div className={styles.commentsModalTitleRow}>
                <FiMessageCircle size={16} />
                <strong>Reply details</strong>
                <span
                  className={`${styles.reportTypeBadge} ${
                    replyPreviewRow.type === "automatic"
                      ? styles.reportTypeAuto
                      : styles.reportTypeManual
                  }`}
                >
                  {replyPreviewRow.type === "automatic" ? "Auto" : "Manual"}
                </span>
              </div>
              <button
                type="button"
                className={styles.ruleTesterCloseButton}
                aria-label="Close"
                onClick={handleCloseReplyPreview}
              >
                <FiX size={18} />
              </button>
            </div>

            <div className={styles.replyContentBody}>
              <div className={styles.replyContentBlock}>
                <span className={styles.ruleBlockType}>
                  {replyPreviewRow.recipientName || "User"}&rsquo;s comment
                </span>
                <span className={styles.replyContentBlockText}>
                  {replyPreviewRow.originalComment
                    ? `"${replyPreviewRow.originalComment}"`
                    : "—"}
                </span>
              </div>

              <div className={styles.replyContentBlock}>
                <span className={styles.ruleBlockType}>Reply sent</span>
                <span className={styles.replyContentBlockText}>
                  {replyPreviewRow.reply}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default InstagramReport;